/* In Residence — the cart engine (Block 4).
 * One small module over Shopify's Ajax Cart API, shared by the night (booking) page
 * and the Basket + Customer Details page, so the taxi rules live in exactly one place.
 *
 * Endpoints are read from window.routes (locale-aware) with the standard fallbacks.
 * Every mutation goes through Shopify; nothing is held in localStorage.
 *
 * 9 Sept 2026 — booking model: a night puts TWO lines in the cart.
 *   slot line  = the arrival-time variant (title "HH:MM"), quantity 1, £0. Counts bookings per slot.
 *   meal line  = the 'Six courses' variant, quantity = diners, £85 a head. Counts covers & is charged.
 * isSlot / isMeal below tell them apart; everything that says "diners" reads the meal line only.
 *
 * 10 Sept 2026 — Pepata: two more variants live on the same "Residency night" product per event,
 * 'Pepata' (the £20 pot, real tracked stock of 10 per event) and 'Pepata — Upgrade to serve 3' (the
 * £10 add-on, untracked — its own legitimacy is enforced here, not by Shopify stock). Both would
 * otherwise be misclassified as meal lines by isMeal's old isNight-and-not-slot test, inflating the
 * diner count — isPapata below carves them out. reconcilePapata is the dependency rule: an event's
 * Upgrade quantity can never exceed that same event's own Pepata quantity, grouped by the 'For'
 * line-item property every Pepata/Upgrade/addon line already carries (the same event label used for
 * wine). It only ever reduces, never adds, and is called after every basket mutation and on load —
 * the same pattern reconcileTaxi already established for the taxi line.
 *
 * 19 Sept 2026 — syncSlotDiners always ended with an unconditional /cart.js re-fetch, even though its
 * own IR.change(o) call (when there was anything to sync) already returns the full, fresh cart, and
 * there was nothing left to do with it when there wasn't. Chained onto ir-basket.liquid's already-
 * sequential run() — which had the same redundant-refetch problem twice over, see that file's 19 Sept
 * note — a single click in the basket was firing four or five sequential requests before the UI updated.
 * On an ordinary connection that was slow enough that a second tap (a guest going 2→3 diners, or
 * clicking Remove again when the first click seemed to do nothing) landed while the UI was still mid-
 * update and was silently dropped (run()'s busy guard). Skipping this fetch entirely — using ops'
 * own last result when there was something to sync, the cart already in hand when there wasn't —
 * removes one more hop from every single basket change.
 *
 * 21 Sept 2026 — updateCartIcon now reports the number of things the guest chose rather than
 * Shopify's raw line count, which included each night's hidden £0 arrival-time slot token. See
 * its own comment below, and snippets/cart-icon-bubble.liquid for the matching server-side rule.
 *
 * 8 Oct 2026 — a slot line now finds ITS meal line, not just any meal line for the same night. Both
 * were matched on product_id alone, so two bookings for one night (say a Hampton and a Molesey booking
 * for 4 Dec, or the same night twice at different times) shared a single pairing: syncSlotDiners wrote
 * the first meal's diner count into every slot, and ir-basket.liquid's Remove took out both slots with
 * either meal, leaving the other booking with no slot - so that quarter-hour's cap could be oversold.
 * ir-night.liquid now stamps one '_booking_id' (newBookingId) on a booking's meal and slot lines, and
 * pairs() below matches on product AND that id. Lines added before this change have no id on either
 * side, so they still pair by product exactly as before. The leading underscore keeps it out of
 * Shopify's checkout and order emails; ir-basket.liquid hides it too.
 *
 * 25 Oct 2026 - ticketed nights (the "arrival time is the ticket" model). A night product with no
 * 'Six courses' variant sells its HH:MM variants as the booking itself: priced per person, quantity =
 * diners, stock = seats for guests arriving at that time. One line per booking, no £0 slot beside it.
 * The only change needed here is that a slot is now an HH:MM line priced £0; a priced HH:MM line is a
 * ticket and is classed as a meal, so everything that reads diners (taxi sizing, the basket count, the
 * Pepata rules) treats it exactly like the 'Six courses' line. Nights still on the two-line model
 * behave exactly as before: their HH:MM slot lines are all £0.
 */
(function(){
  var R=window.routes||{};
  var U={
    cart:(R.cart_url||'/cart')+'.js',
    add:(R.cart_add_url||'/cart/add')+'.js',
    change:(R.cart_change_url||'/cart/change')+'.js',
    update:(R.cart_update_url||'/cart/update')+'.js'
  };
  function parse(r){ return r.text().then(function(t){ var j=null; try{ j=JSON.parse(t); }catch(e){}
      if(!r.ok||!j){ var msg=r.status===429?'Too many changes at once - give it a second and try again.':(j&&(j.description||j.message))||'The basket didn’t respond - please try again.'; throw Object.assign(new Error(msg),{status:r.status,payload:j}); }
      return j; }); }
  function post(url,body){
    return fetch(url,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(body)}).then(parse);
  }
  var SLOT_RE=/^\d{2}:\d{2}$/;
  var PAPATA_RE=/^Pepata/;
  var IR={
    /* Configured once per page by Liquid: { small:id, mid:id, xl:id, handle:'taxi-service' } */
    taxi:null,
    configure:function(cfg){ IR.taxi=Object.assign({},IR.taxi||{},cfg||{}); },
    get:function(){ return fetch(U.cart,{headers:{'Accept':'application/json'}}).then(parse); },
    /* Note: /cart.js does not support ?sections= (that's only for the AJAX Cart mutation
     * endpoints, or ?section_id= on a full page route). A no-op /cart/update.js call is the
     * supported way to fetch fresh section HTML without guessing at a line change. */
    getWithSections:function(sectionIds){ return post(U.update,{updates:{},sections:sectionIds}); },
    add:function(items,sections){ var b={items:items}; if(sections) b.sections=sections; return post(U.add,b).then(IR.updateCartIcon); },
    change:function(body){ return post(U.change,body).then(IR.updateCartIcon); },
    update:function(body){ return post(U.update,body).then(IR.updateCartIcon); },
    /* 21 Sept 2026 — two changes, both so the header agrees with what the guest actually chose.
     * (1) The number no longer uses cart.item_count, which counts the hidden £0 arrival-time slot
     *     token every night carries as if it were a product: two meals read as "Basket · 3".
     *     basketCount below skips slot lines, exactly as snippets/cart-icon-bubble.liquid now does
     *     server-side, so both render the same figure.
     * (2) It writes the whole label rather than appending Dawn's .cart-count-bubble <div>. That
     *     bubble was a leftover from the stock theme: this header renders the word "Basket · N"
     *     from the snippet, so appending a second element put the number on the page twice. */
    basketCount:function(cart){
      return (cart.items||[]).filter(function(l){ return !IR.isSlot(l); })
                             .reduce(function(s,l){ return s+l.quantity; },0);
    },
    updateCartIcon:function(cart){
      if(!cart) return cart;
      var link=document.getElementById('cart-icon-bubble'); if(!link) return cart;
      var count=IR.basketCount(cart);
      link.textContent='Basket'+(count>0?' · '+count:'');
      return cart;
    },
    isTaxi:function(line){ return !!IR.taxi && (line.handle===IR.taxi.handle || [IR.taxi.small,IR.taxi.mid,IR.taxi.xl].indexOf(line.variant_id)>=0); },
    isNight:function(line){ return line.product_type==='Residency night'; },
    unitPrice:function(line){ return line.original_price!=null?line.original_price:line.price; },
    isSlot:function(line){ return IR.isNight(line) && SLOT_RE.test(line.variant_title||'') && !(IR.unitPrice(line)>0); },
    isTicket:function(line){ return IR.isNight(line) && SLOT_RE.test(line.variant_title||'') && IR.unitPrice(line)>0; },
    isPapata:function(line){ return IR.isNight(line) && PAPATA_RE.test(line.variant_title||''); },
    isPapataUpgrade:function(line){ return IR.isPapata(line) && /Upgrade/.test(line.variant_title||''); },
    isPapataStandard:function(line){ return IR.isPapata(line) && !IR.isPapataUpgrade(line); },
    isMeal:function(line){ return IR.isNight(line) && !IR.isSlot(line) && !IR.isPapata(line); },
    taxiLines:function(cart){ return (cart.items||[]).filter(IR.isTaxi); },
    diners:function(cart){ var n=(cart.items||[]).filter(IR.isMeal).reduce(function(s,l){ return s+l.quantity; },0); return Math.min(6,Math.max(0,n)); },
    bookingId:function(line){ return (line.properties||{})['_booking_id']||''; },
    newBookingId:function(){ return Date.now().toString(36)+Math.random().toString(36).slice(2,8); },
    pairs:function(slotLine,mealLine){ return slotLine.product_id===mealLine.product_id && IR.bookingId(slotLine)===IR.bookingId(mealLine); },
    slotsFor:function(cart,mealLine){ return (cart.items||[]).filter(function(l){ return IR.isSlot(l) && IR.pairs(l,mealLine); }); },
    papataEventKey:function(line){ return (line.properties||{})['For']||''; },
    reconcilePapata:function(cart){
      var up=(cart.items||[]).filter(IR.isPapataUpgrade); if(!up.length) return Promise.resolve(cart);
      var stdByEvent={};
      (cart.items||[]).filter(IR.isPapataStandard).forEach(function(l){
        var k=IR.papataEventKey(l); stdByEvent[k]=(stdByEvent[k]||0)+l.quantity;
      });
      var updates={}, dirty=false;
      up.forEach(function(l){
        var k=IR.papataEventKey(l), allowed=stdByEvent[k]||0;
        if(l.quantity>allowed){ updates[l.key]=allowed; dirty=true; }
      });
      if(!dirty) return Promise.resolve(cart);
      return IR.update({updates:updates});
    },
    taxiVariantFor:function(diners){
      if(!IR.taxi) return null;
      if(diners<=2) return IR.taxi.small;
      if(diners<=4) return IR.taxi.mid;
      return IR.taxi.xl;
    },
    taxiCompatible:function(cart,diners){
      var t=IR.taxiLines(cart); if(!t.length) return true;
      return t.length===1 && t[0].quantity===1 && t[0].variant_id===IR.taxiVariantFor(diners);
    },
    reconcileTaxi:function(cart,diners){
      var t=IR.taxiLines(cart); if(!t.length) return Promise.resolve(cart);
      var want=IR.taxiVariantFor(diners), updates={}, dirty=false;
      [IR.taxi.small,IR.taxi.mid,IR.taxi.xl].forEach(function(id){
        var line=t.filter(function(l){ return l.variant_id===id; })[0];
        var q=(id===want)?1:0;
        if((line?line.quantity:0)!==q){ updates[id]=q; dirty=true; }
      });
      if(!dirty) return Promise.resolve(cart);
      return IR.update({updates:updates});
    },
    addTaxi:function(cart,diners){
      if(IR.taxiLines(cart).length) return IR.reconcileTaxi(cart,diners);
      var id=IR.taxiVariantFor(diners); if(!id) return Promise.reject(new Error('taxi not configured'));
      var u={}; u[id]=1; return IR.update({updates:u});
    },
    removeTaxi:function(cart){
      var u={}, any=false; IR.taxiLines(cart).forEach(function(l){ u[l.key]=0; any=true; });
      return any?IR.update({updates:u}):Promise.resolve(cart);
    },
    syncSlotDiners:function(cart){
      var ops=[];
      (cart.items||[]).filter(IR.isSlot).forEach(function(sl){
        var meal=(cart.items||[]).filter(function(l){ return IR.isMeal(l) && IR.pairs(sl,l); })[0];
        if(!meal) return;
        var want=String(meal.quantity);
        if((sl.properties||{}).Diners!==want){
          var props=Object.assign({},sl.properties||{},{Diners:want});
          ops.push({id:sl.key,quantity:1,properties:props});
        }
      });
      if(!ops.length) return Promise.resolve(cart);
      return ops.reduce(function(p,o){ return p.then(function(){ return IR.change(o); }); },Promise.resolve(cart));
    },
    setAttributes:function(attrs){ return IR.update({attributes:attrs}); },
    fmt:function(p){ var v=p/100; return '£'+(v%1===0?v.toFixed(0):v.toFixed(2)); }
  };
  window.IRCart=IR;
})();